import moment from 'moment';
import {
  ICantUpdateItems,
  IMaterialSelected,
  ISelectedTableOrders,
  UpdateErrorType,
  ValidationErrorEnum
} from '.';
import { IOpenOrdersTableDataSource } from '..';
import {
  EOpenColumn,
  IOrdersChageDataResponse,
  IOrdersChangeData,
  IUpdateOrderRequest,
  IUpdateOrderResponse,
  IUpdateOrderValidation,
  IUpdateOrderValidationResponse
} from '../../../../../../../store/ducks/long-steel-orders/types';
import { IUserOrderItemPreference } from '../../../../../../../store/ducks/user/types';

export const addBusinessDays = (numDaysToAdd: number) => {
  const Sunday = 0;
  const Saturday = 6;
  let daysRemaining = numDaysToAdd;

  const newDate = moment();

  while (daysRemaining > 0) {
    newDate.add(1, 'days');
    if (newDate.day() !== Sunday && newDate.day() !== Saturday) {
      daysRemaining--;
    }
  }
  return newDate.format('YYYY-MM-DD');
};

export const addCharactersToString = (
  str: string,
  char: string,
  length: number
): string => {
  if (length <= str.length) {
    return str;
  }
  let newStr = str;
  while (newStr.length < length) {
    newStr = char.concat(newStr);
  }
  return newStr;
};

export const formatSelectedItens = (
  tableSelectedItems: IOpenOrdersTableDataSource[],
  validatedItems: IUpdateOrderValidationResponse[],
  itemsMultiples: IOrdersChageDataResponse[],
  userOrderPreference: IUserOrderItemPreference
): ISelectedTableOrders[] => {
  const selectedItems: ISelectedTableOrders[] = [];
  tableSelectedItems.forEach((tableItem) => {
    const validationItem = validatedItems.find(
      (item) => item.orderId === tableItem.key
    );
    if (validationItem) {
      const multiple = itemsMultiples.find(
        (item) => item.sku === tableItem.sku
      );
      const requestedMeasurement = multiple?.measureUnits.find(
        (mu) => mu.measure === tableItem.quantities.requestedMeasureUnit
      );
      const orderInArray = selectedItems.findIndex((item) => {
        return (
          item.orderNumber === tableItem.customerOrder ||
          item.orderNumber === tableItem.salesOrder
        );
      });
      if (orderInArray !== -1) {
        selectedItems[orderInArray].materials.push({
          id: tableItem.key,
          description: tableItem.materialDescription,
          orderedQuantity: tableItem.quantities,
          requestedDate: tableItem.requestedDate,
          customerId: tableItem.item.soldTo.code,
          changeAllowed: validationItem.changeAllowed,
          reasonNotAllowed: validationItem.reasonNotAllowed,
          measurement: requestedMeasurement,
          salesOrder: addCharactersToString(tableItem.salesOrder, '0', 10),
          salesOrderItem: addCharactersToString(
            tableItem.salesOrderItem,
            '0',
            6
          )
        });
        return;
      }
      selectedItems.push({
        salesOrder: addCharactersToString(tableItem.salesOrder, '0', 10),
        orderNumber:
          userOrderPreference.idOrder === EOpenColumn.ORDER_NUMBER
            ? tableItem.customerOrder
            : tableItem.salesOrder,
        materials: [
          {
            id: tableItem.key,
            description: tableItem.materialDescription,
            orderedQuantity: tableItem.quantities,
            requestedDate: tableItem.requestedDate,
            customerId: tableItem.item.soldTo.code,
            changeAllowed: validationItem.changeAllowed,
            reasonNotAllowed: validationItem.reasonNotAllowed,
            measurement: requestedMeasurement,
            salesOrder: addCharactersToString(tableItem.salesOrder, '0', 10),
            salesOrderItem: addCharactersToString(
              tableItem.salesOrderItem,
              '0',
              6
            )
          }
        ]
      });
    }
  });
  return selectedItems;
};

export const formatChangeValidationData = (
  tableSelectedItems: IOpenOrdersTableDataSource[]
): [IUpdateOrderValidation[], IOrdersChangeData[]] => {
  const validationItems: IUpdateOrderValidation[] = [];
  const dataItems: IOrdersChangeData[] = [];
  tableSelectedItems.forEach((tableItem) => {
    validationItems.push({
      customer: tableItem.item.soldTo.code,
      orderId: tableItem.key.toString()
    });
    dataItems.push({
      shipTo: tableItem.item.shipTo.code,
      soldTo: tableItem.item.soldTo.code,
      details: [
        {
          gerdauSku: tableItem.sku
        }
      ]
    });
  });
  return [validationItems, dataItems];
};

export const formatRequestBody = (
  selectedTableItems: ISelectedTableOrders[],
  option: string,
  cancelMessage?: string,
  otherMessage?: string
): IUpdateOrderRequest[] => {
  const items = selectedTableItems.flatMap((item) => item.materials);
  return items.reduce((acc: IUpdateOrderRequest[], item) => {
    if (
      option !== 'otherChip' &&
      (!item.changeAllowed || item.errorType === UpdateErrorType.STATUS)
    ) {
      return acc;
    }
    const requestItem: IUpdateOrderRequest = {
      customer: item.customerId,
      customerType: 'soldTo',
      salesOrder: item.salesOrder,
      salesOrderItem: item.salesOrderItem
    };
    if (option === 'volumeChip') {
      requestItem.quantity = item.newVolume?.replace('.', ',');
      requestItem.unityMeasure = item.measurement?.measure;
    } else if (option === 'deliveryChip') {
      requestItem.requiredDate = item.newDate;
    } else if (option === 'cancelChip') {
      requestItem.status = 'cancelado';
      requestItem.reasonMessage = cancelMessage?.trim();
    } else if (option === 'otherChip') {
      requestItem.editMessage = otherMessage?.trim();
    }
    acc.push(requestItem);
    return acc;
  }, []);
};

export const formatOnRequestError = (
  selectedItems: ISelectedTableOrders[],
  errorItems: IUpdateOrderResponse[]
): ISelectedTableOrders[] => {
  const newSelectedItem = JSON.parse(
    JSON.stringify(selectedItems)
  ) as ISelectedTableOrders[];
  errorItems.forEach((error) => {
    const si = newSelectedItem.find((i) => i.salesOrder === error.salesOrder);
    const material = si?.materials.find(
      (m) => m.salesOrderItem === error.salesOrderItem
    );
    if (material && error.errors.length) {
      material.errorType = error.errors[0].attribute;
      if (error.errors[0].attribute === UpdateErrorType.ITEM) {
        const splitMsg = error.errors[0].errorMessage.split(/\b(pois)/);
        material.updateErrorMessage = splitMsg[splitMsg.length - 1];
      }
    }
  });
  return newSelectedItem;
};

export const groupErrors = (
  materials: IMaterialSelected[]
): ICantUpdateItems => {
  return materials.reduce((result: ICantUpdateItems, material) => {
    if (!material.changeAllowed) {
      const reason: ValidationErrorEnum =
        material.reasonNotAllowed ?? ValidationErrorEnum.DEFAULT;
      if (!result[reason]) {
        result[reason] = [];
      }
      result[reason].push(material.description);
    }
    return result;
  }, {} as ICantUpdateItems);
};

export const mergeGroupedErrors = (
  previousResult: ICantUpdateItems,
  newResult: ICantUpdateItems
): ICantUpdateItems => {
  for (const key in newResult) {
    if (Object.prototype.hasOwnProperty.call(newResult, key)) {
      if (!previousResult[key as ValidationErrorEnum]) {
        previousResult[key as ValidationErrorEnum] = [];
      }
      previousResult[key as ValidationErrorEnum] = [
        ...previousResult[key as ValidationErrorEnum],
        ...newResult[key as ValidationErrorEnum]
      ];
    }
  }
  return previousResult;
};

export const countErrors = (groupedErrors: ICantUpdateItems): number => {
  return Object.entries(groupedErrors).reduce((count: number, m): number => {
    return (count += m[1].length);
  }, 0);
};
